<script setup>
  import { ref, computed, nextTick, onMounted, onBeforeUnmount, inject } from 'vue'
  import { useRouter, useRoute } from 'vue-router'
  import { useAuthStore } from '../shared/stores/AuthStore'
  import { useLogout } from '../shared/composables/useLogout'
  import { getAuth } from 'firebase/auth'
  import { RequestService } from '../shared/services/RequestService'
  import logo from '@/assets/images/loginLogo.png'
  import { handleErrors } from '../shared/utils/errorHandler'
  import { EmailUtilsService } from '../shared/services/EmailUtilsService'
  import { useTemplateVariablesStore } from '../shared/stores/TemplateVariablesStore'
  const { getEmailTemplate, getEmailRecipient } = EmailUtilsService()
  const { post } = RequestService()
  const functionsBaseUrl = inject('functionsBaseUrl')
  const router = useRouter()
  const route = useRoute()
  const authStore = useAuthStore()
  const templateStore = useTemplateVariablesStore()
  const { resetTemplateData } = templateStore

  const showError = ref(false)
  const usernameError = ref(false)
  const showPass = ref(false)
  const showSuccessMessage = ref(false)
  const loading = ref(false)
  const buttonLoading = ref(false)
  const showResetForm = ref(false)

  /* ── Two-factor step ──────────────────────────────────────────────────────
   * Lives in this same view as a third branch of the card, next to the reset-password
   * form that is already hosted here. No new route: a separate page would be bounced
   * back to home by the router's authenticated-on-public rule, since the Firebase
   * session already exists by the time the code is asked for. */
  const { logout: fullLogout } = useLogout()
  const mfaAccountEmail = ref('')
  const mfaVerified = ref(false)
  const step = ref('credentials')          // 'credentials' | 'code'
  const MFA_DIGITS = 6
  const mfaDigits = ref(Array(MFA_DIGITS).fill(''))
  const mfaError = ref('')
  const mfaSending = ref(false)
  const mfaVerifying = ref(false)
  const mfaLockedUntil = ref(0)
  const mfaResendAt = ref(0)
  const nowTick = ref(Date.now())
  let mfaTicker = null

  const mfaCode = computed(() => mfaDigits.value.join(''))
  const isMfaCodeComplete = computed(() => mfaCode.value.length === MFA_DIGITS)

  const isMfaLocked = computed(() => mfaLockedUntil.value > nowTick.value)
  /* Reads as a wait, not as a number: under a minute, "1 minute(s)" overstates a pause that is
   * about to lift, and "0 minute(s)" would look broken. */
  const mfaLockCountdown = computed(() => {
    const remaining = mfaLockedUntil.value - nowTick.value
    return remaining <= 60000
      ? 'in less than a minute'
      : `in ${Math.ceil(remaining / 60000)} minute(s)`
  })
  const mfaResendIn = computed(() => Math.max(0, Math.ceil((mfaResendAt.value - nowTick.value) / 1000)))
  const canResendMfa = computed(() =>
    !mfaSending.value && !mfaVerifying.value && !isMfaLocked.value && mfaResendIn.value === 0)
  const canSubmitMfa = computed(() =>
    isMfaCodeComplete.value && !mfaVerifying.value && !isMfaLocked.value)

  /* One attribute QA (and anyone debugging) can wait on, instead of fixed timeouts. */
  const mfaState = computed(() => {
    if (isMfaLocked.value) return 'locked'
    if (mfaVerifying.value) return 'verifying'
    if (mfaSending.value) return 'sending'
    if (mfaError.value) return 'error'
    return 'ready'
  })

  const MFA_ERROR_COPY = {
    mfa_code_invalid: "That code isn't right. Check the email and try again.",
    mfa_code_expired: 'That code has expired. Send a new one to continue.',
    mfa_code_consumed: 'That code was already used. Send a new one to continue.',
    mfa_no_challenge: 'No code is pending. Send a new one to continue.',
    mfa_session_mismatch: 'That code belongs to a different sign-in. Send a new one.',
    mfa_locked: 'Too many incorrect codes. For your security, this sign-in is paused.',
    mfa_resend_cooldown: 'A code was just sent. Please wait before requesting another.',
    mfa_resend_exhausted: 'Too many codes requested. Sign out and sign in again to start over.',
    mfa_send_failed: "We couldn't send your code. Please try again in a moment.",
    mfa_session_unavailable: "Your code was accepted, but we couldn't finish signing you in. Please try again.",
    mfa_not_configured: 'Two-factor authentication is unavailable right now. Please contact BCS Support.',
    too_many_requests: 'Too many attempts. Please wait a moment before trying again.'
  }
  const resetEmailSent = ref(false)
  const resetEmail = ref('')
  const resetEmailError = ref(false)
  const successMessage = ref('')
  

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const formState = ref({
    username: '',
    password: '',
    returnUrl: '/tmplogin.html',
    service: 'login',
    Login: 'Login'
  })

  const closeSuccessMessage = () => {
    showSuccessMessage.value = false
  }

  const toggleResetForm = () => {
    showResetForm.value = !showResetForm.value
    resetEmailSent.value = false
    resetEmail.value = formState.value.username
    showError.value = false
    resetEmailError.value = false
    closeSuccessMessage()
  }

  const sendResetEmail = async () => {
    if (!resetEmail.value) return

    resetEmailError.value = false

    if (!validateEmail(resetEmail.value)) {
      resetEmailError.value = true
      return
    }

    buttonLoading.value = true
    showError.value = false

    try {
      // Send custom email through Firebase function
      await post('sendEmail', `${functionsBaseUrl}/utilityGateway/api/v1/email/send`, {
        type: 'AUTH',
        subtype: 'PASSWORD_RESET',
        email: resetEmail.value,
        emailTemplate: await getEmailTemplate('PASSWORD_RESET'),
        emailRecipient: await getEmailRecipient('TEST_EMAIL_RECIPIENT')
      })
    } catch (err) {
      showError.value = false
    } finally {
      resetEmailSent.value = true
      buttonLoading.value = false
    }
  }

  // const sendResetToNewEmail = () => {
  //   resetEmailSent.value = false
  //   resetEmail.value = ''
  // }

  async function login() {
    try {
      const { success, mfaRequired } = await authStore.login(formState.value.username, formState.value.password)

      if (!success) {
        showError.value = true
        formState.value.password = ''
        return
      }

      if (mfaRequired) {
        await enterMfaStep()
        return
      }

      resetTemplateData()
      router.push({ name: 'home', query: route.query })
    } catch (error) {
      showError.value = true
      formState.value.password = ''
    }
  }

  /* ── Two-factor step ─────────────────────────────────────────────────── */

  /* Sets the pause and re-syncs the tick in the same breath. The ticker only moves once a second,
   * so a fresh deadline compared against a stale tick reads as more than it is — a 2-minute pause
   * was rendering as "3 minute(s)". */
  function setMfaLockedUntil(until) {
    nowTick.value = Date.now()
    mfaLockedUntil.value = until || 0
  }

  function startMfaTicker() {
    if (mfaTicker) return
    mfaTicker = setInterval(() => { nowTick.value = Date.now() }, 1000)
  }

  onBeforeUnmount(() => {
    if (mfaTicker) clearInterval(mfaTicker)
  })

  async function enterMfaStep() {
    step.value = 'code'
    mfaVerified.value = false
    mfaAccountEmail.value = getAuth().currentUser?.email || ''
    showError.value = false
    showResetForm.value = false
    // The password has done its job; do not keep it in memory through the second step.
    formState.value.password = ''
    startMfaTicker()
    /* Restore a pause that is still running on the server. GET /mfa/status already ran before we
     * got here (the router guard and onMounted both call refreshMfaPending), so this costs no
     * round trip — and without it a reload comes back with the boxes enabled and no banner, even
     * though every attempt is still going to be refused. */
    setMfaLockedUntil(authStore.mfaLockedUntil)
    await ensureMfaChallenge()
    // The first box takes focus on its own: the only thing to do here is type.
    await nextTick()
    focusMfaBox(0)
  }

  /** Reuses a live challenge, so a reload (or a second tab) does not trigger another email. */
  async function ensureMfaChallenge() {
    // Paused: the challenge endpoint refuses too, so asking would only produce a stray 423.
    if (isMfaLocked.value) return
    const pending = authStore.readMfaChallengeState()
    if (pending && pending.expiresAt > Date.now()) {
      mfaResendAt.value = pending.resendAvailableAt || 0
      return
    }
    await sendMfaChallenge()
  }

  async function sendMfaChallenge() {
    // Guards the handler itself, not just the link: paste, Enter and a stale click all land here.
    if (isMfaLocked.value) return
    mfaSending.value = true
    mfaError.value = ''
    try {
      const record = await authStore.requestMfaChallenge()
      if (record.alreadyVerified) {
        await finishMfa()
        return
      }
      mfaResendAt.value = record.resendAvailableAt
      clearMfaDigits()
      // A resent code starts from the first box too.
      await nextTick()
      focusMfaBox(0)
    } catch (err) {
      applyMfaError(err)
    } finally {
      mfaSending.value = false
    }
  }

  function focusMfaBox(index) {
    const target = document.getElementById(`mfaCode${Math.min(Math.max(index, 0), MFA_DIGITS - 1)}`)
    if (target) {
      target.focus()
      target.select?.()
    }
  }

  function clearMfaDigits() {
    mfaDigits.value = Array(MFA_DIGITS).fill('')
  }

  /**
   * Accepts one digit per box, but also a whole code typed or pasted into any box (the email
   * may carry it as "12 34 56", and Playwright fills the first box with all six).
   */
  function onMfaDigitInput(index, event) {
    const digits = String(event.target.value).replace(/\D/g, '')
    if (mfaError.value) mfaError.value = ''

    if (digits.length <= 1) {
      mfaDigits.value[index] = digits
      if (digits) focusMfaBox(index + 1)
    } else {
      const spread = digits.slice(0, MFA_DIGITS - index).split('')
      spread.forEach((digit, offset) => { mfaDigits.value[index + offset] = digit })
      focusMfaBox(index + spread.length)
    }

    // Keeps the DOM in step when the value was rejected (a letter) or redistributed.
    event.target.value = mfaDigits.value[index] || ''
    maybeAutoSubmitMfa()
  }

  function onMfaDigitKeydown(index, event) {
    if (event.key === 'Backspace' && !mfaDigits.value[index] && index > 0) {
      event.preventDefault()
      mfaDigits.value[index - 1] = ''
      focusMfaBox(index - 1)
    }
    if (event.key === 'ArrowLeft') { event.preventDefault(); focusMfaBox(index - 1) }
    if (event.key === 'ArrowRight') { event.preventDefault(); focusMfaBox(index + 1) }
  }

  function onMfaPaste(index, event) {
    const pasted = String(event.clipboardData?.getData('text') || '').replace(/\D/g, '')
    if (!pasted) return
    event.preventDefault()
    const spread = pasted.slice(0, MFA_DIGITS - index).split('')
    spread.forEach((digit, offset) => { mfaDigits.value[index + offset] = digit })
    if (mfaError.value) mfaError.value = ''
    focusMfaBox(index + spread.length)
    maybeAutoSubmitMfa()
  }

  /* The screen promises it submits on its own; the button stays for keyboard and for retrying. */
  function maybeAutoSubmitMfa() {
    if (canSubmitMfa.value) submitMfaCode()
  }

  async function submitMfaCode() {
    if (!canSubmitMfa.value) return
    mfaVerifying.value = true
    mfaError.value = ''
    let refocusFirstBox = false
    try {
      await authStore.verifyMfaCode(mfaCode.value)
      // Resending makes no sense any more; the controls go away before we navigate.
      mfaVerified.value = true
      await finishMfa()
    } catch (err) {
      /* The code was accepted but the session could not be started. The digits stay in place so
       * the button can be pressed again; the store skips the spent code and retries only the
       * session exchange. */
      if (err?.message === 'mfa_session_unavailable') {
        mfaError.value = MFA_ERROR_COPY.mfa_session_unavailable
        return
      }
      applyMfaError(err)
      clearMfaDigits()
      refocusFirstBox = true
    } finally {
      mfaVerifying.value = false
      /* The boxes are disabled while verifying, so focusing has to wait until that flag is
       * down and Vue has re-rendered them — otherwise focus() silently does nothing. */
      if (refocusFirstBox) {
        await nextTick()
        focusMfaBox(0)
      }
    }
  }

  async function finishMfa() {
    resetTemplateData()
    router.push({ name: 'home', query: route.query })
  }

  function applyMfaError(err) {
    const body = err?.response?.data || {}
    /* The IP limiter answers 429 with a deliberately silent body ({ success: true }) so it does
     * not leak its state, which leaves no code to read. Treat any uncoded 429 as the same
     * "wait a moment" outcome as the per-user limiter, rather than the generic failure. */
    // Store-raised errors carry their code in the message (e.g. mfa_session_unavailable).
    const code = body.error?.code || body.code
      || (err?.response?.status === 429 ? 'too_many_requests' : '')
      || err?.message || ''
    mfaError.value = MFA_ERROR_COPY[code]
      || 'Something went wrong. Please try again, or contact BCS Support if this keeps happening.'

    if (code === 'mfa_locked') {
      const seconds = Number(body.retryAfterSeconds) || 900
      setMfaLockedUntil(Date.now() + seconds * 1000)
    }
    if (code === 'mfa_resend_cooldown' && Number(body.retryAfterSeconds)) {
      mfaResendAt.value = Date.now() + Number(body.retryAfterSeconds) * 1000
    }
  }

  async function signOutFromMfa() {
    authStore.clearMfaChallengeState()
    step.value = 'credentials'
    clearMfaDigits()
    mfaError.value = ''
    setMfaLockedUntil(0)
    await fullLogout()
  }

  onMounted(async () => {
    /* Reload or deep link while a code is pending: the Firebase session already exists, so the
     * guard routed here. Resume the step instead of showing the credentials form again. */
    if (await authStore.refreshMfaPending()) {
      await enterMfaStep()
      return
    }

    const status = route.query.status
    const resetPass = route.query.resetPass
    const encodedEmail = route.query.e

    if (status === 'created' || status === 'updated') {
      showSuccessMessage.value = true
      successMessage.value =
        status === 'updated' ?
          `You've successfully updated your password!`
          : `You've successfully created your password!`
    }

     if (resetPass === 'true') {
      showResetForm.value = true
      if (encodedEmail) {
        try {
          resetEmail.value = atob(encodedEmail)           
        } catch (e) {
          handleErrors('Invalid email encoding', e)
        }
      }
    }
  })
</script>

<template>
  <div class="bg-white loading-overlay" v-if="loading">
    <div class="spinner-border" role="status">
      <span class="visually-hidden">Loading...</span>
    </div>
  </div>

  <div class="container-fluid bg-dark" :class="step === 'code' ? 'login-shell--mfa' : 'pt-5 px-5'">
    <div
      class="row d-flex justify-content-center login-card-container"
      :class="{ 'pt-3': step !== 'code', 'login-card-container--mfa': step === 'code' }">
      <div
        class="container bg-white rounded login-card"
        :class="step === 'code' ? 'login-card--mfa' : 'pb-2 pt-5'">
        <div v-if="showSuccessMessage" class="pb-1 row d-flex justify-content-center">
          <div
            class="alert alert-success alert-dismissible fade show col-md-8 shadow-sm"
            role="alert">
            <div class="d-flex align-items-center">
              <i class="bi bi-check-circle-fill me-2"></i>
              <div class="text-14">
                <strong>
                  {{ successMessage }}
                </strong>
              </div>
            </div>
            <button
              type="button"
              class="btn-close"
              @click="closeSuccessMessage"
              aria-label="Close"></button>
          </div>
        </div>
        <div
          class="row d-flex justify-content-center"
          :class="step === 'code' ? 'mfa-logo-row' : 'mt-4'">
          <div class="col-md-6 text-center">
            <img id="logo" :src="logo" class="img-fluid" alt="bcs logo" />
          </div>
        </div>
        <!-- Two-factor step. First branch so it wins over any reset-form state. -->
        <div v-if="step === 'code'" id="mfaStep" :data-mfa-state="mfaState">
          <div class="mfa-header">
            <div class="text-28"><med>Check your email</med></div>
            <p class="mfa-header-text mb-0">
              We sent a 6-digit code to
              <span id="mfaSentTo" class="mfa-account-email">{{ mfaAccountEmail }}</span>.
              It expires in {{ authStore.mfaExpiryMinutes }} minutes.
            </p>
          </div>

          <div class="row d-flex justify-content-center">
            <div class="col-md-8">
              <form class="mfa-form" @submit.prevent="submitMfaCode">
                <div class="mfa-field">
                  <!-- Level 1: label. -->
                  <div class="mfa-field-header">
                    <label for="mfaCode0" class="form-label text-14 mb-0">
                      <strong>Verification code</strong>
                    </label>
                  </div>

                  <!-- Level 2: one box per digit. -->
                  <div id="mfaCodeBoxes" class="mfa-digits" :class="{ 'mfa-digits--error': Boolean(mfaError) }">
                    <input
                      v-for="(digit, index) in mfaDigits"
                      :key="index"
                      :id="`mfaCode${index}`"
                      class="mfa-digit"
                      :class="{ 'mfa-digit--error': Boolean(mfaError) }"
                      type="text"
                      inputmode="numeric"
                      :autocomplete="index === 0 ? 'one-time-code' : 'off'"
                      :aria-label="`Digit ${index + 1} of ${MFA_DIGITS}`"
                      :value="digit"
                      :disabled="mfaVerifying || isMfaLocked"
                      @input="onMfaDigitInput(index, $event)"
                      @keydown="onMfaDigitKeydown(index, $event)"
                      @paste="onMfaPaste(index, $event)"
                      @focus="$event.target.select()" />
                  </div>

                  <!-- Level 3: hint until the code is complete, then the error if it fails
                       (an expired code just comes back as a normal submit error). -->
                  <p
                    v-if="mfaError"
                    id="mfaError"
                    class="mfa-help mfa-help--error mb-0"
                    role="alert">
                    <i class="bi bi-exclamation-circle-fill me-1"></i>{{ mfaError }}
                  </p>
                  <p
                    v-else-if="!isMfaCodeComplete"
                    id="mfaHelp"
                    class="mfa-help mb-0">
                    Paste the whole code, or type it. It submits automatically.
                  </p>
                </div>

                <div
                  v-if="isMfaLocked"
                  id="mfaLockout"
                  class="alert alert-warning text-center text-14"
                  role="alert">
                  Too many incorrect codes. For your security, this sign-in is paused.
                  Try again {{ mfaLockCountdown }}.
                </div>
                <button
                  id="mfaSubmitButton"
                  class="btn btn-primary w-100 header-20"
                  :disabled="!canSubmitMfa"
                  type="submit">
                  <div
                    v-if="mfaVerifying"
                    class="spinner-border spinner-border-sm me-2"
                    role="status" />
                  <med>Verify and Sign In</med>
                </button>

                <!-- Below the divider everything is secondary help, left aligned. -->
                <div class="mfa-footer">
                  <p class="mfa-footer-line mb-0">
                    Didn't get the email?
                    <span v-if="mfaVerified"></span>
                    <span
                      v-else-if="isMfaLocked"
                      id="mfaResendPaused"
                      class="mfa-resend-waiting">
                      Send a new code (paused)
                    </span>
                    <span
                      v-else-if="!canResendMfa"
                      id="mfaResendCooldown"
                      class="mfa-resend-waiting">
                      Send a new code ({{ mfaResendIn }}s)
                    </span>
                    <a
                      v-else
                      id="mfaResendButton"
                      href="#"
                      class="mfa-resend-link"
                      @click.prevent="sendMfaChallenge">Send a new code</a>
                  </p>
                  <p class="mfa-footer-line mb-0">
                    Need to switch accounts?
                    <a id="mfaSignOutLink" href="#" class="mfa-signout-link" @click.prevent="signOutFromMfa">Go back</a>
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>

        <div v-else-if="showResetForm">
          <div v-if="!resetEmailSent">
            <div class="text-center mt-4 text-28"><med>Reset Your Password</med></div>
            <p class="text-center text-14">
              We'll send you an email with instructions to reset your password.
            </p>

            <div class="row d-flex justify-content-center">
              <div class="col-md-8">
                <!-- Email Input -->
                <div class="mb-3">
                  <label for="resetEmail" class="form-label text-14">
                    <strong>Email</strong>
                  </label>
                  <input
                    type="email"
                    class="form-control"
                    :class="{ 'is-invalid': resetEmailError }"
                    id="resetEmail"
                    v-model="resetEmail"
                    :readonly="route.query.e"
                    placeholder="Enter your email"
                    :disabled="buttonLoading || route.query.e" />
                  <div v-if="resetEmailError" class="invalid-feedback">
                    Please enter a valid email address
                  </div>
                </div>

                <!-- Submit Button -->
                <button
                  @click="sendResetEmail"
                  class="btn btn-primary w-100 mb-3 header-20"
                  :disabled="!resetEmail || buttonLoading"
                  type="button">
                  <div
                    v-if="buttonLoading"
                    class="spinner-border spinner-border-sm me-2"
                    role="status" />
                  <med>Send Reset Password Email</med>
                </button>

                <!-- Back to Login -->
                <div  class="text-center pb-5">
                  <a v-if="!route.query.resetPass" href="#" class="text-14" @click.prevent="toggleResetForm">
                    <med>Back to Login</med>
                  </a>
                </div>
              </div>
            </div>
          </div>

          <!-- Reset Email Sent -->
          <div v-else class="text-center mt-4">
            <div class="text-28 mt-3 mb-4 px-5"><med>If your email exists on the system you'll be receiving a reset link shortly.</med></div>

            <!-- <a 
              href="#" 
              class="d-block mb-4" 
              @click.prevent="sendResetToNewEmail"
            >
              Send Reset Link to a Different Email
            </a> -->

            <button
              v-if="!route.query.resetPass"
              @click="toggleResetForm"
              class="btn btn-primary w-100 mb-3 mx-auto"
              style="max-width: 300px">
              Back to Login
            </button>

            <p class="text-14 mt-3">
              If you don't receive the email, please
              <a
                href="mailto:info@bcspro.com?subject=Reset Password Email Not Received"
                class="text-14">
                contact BCS Support
              </a>
            </p>
          </div>
        </div>

        <div v-else>
          <div class="row mt-3">
            <div class="col-12 text-center my-4 text-28">
              <med>Welcome! Please sign in.</med>
            </div>
          </div>
          <div class="row d-flex justify-content-center mt-2">
            <div class="col-md-9">
              <form @submit.prevent="login">
                <div class="card px-3" id="form1">
                  <div class="form-data">
                    <div class="forms-inputs mb-3">
                      <label for="username" class="form-label text-14">
                        <strong>Email</strong>
                      </label>
                      <input
                        id="username"
                        autocomplete="off"
                        class="form-control text-14"
                        type="email"
                        placeholder="Enter email"
                        v-model="formState.username"
                        required />
                      <div
                        id="invalidMailMessage"
                        v-if="usernameError && formState.username.length > 0"
                        class="alert alert-danger text-center"
                        role="alert">
                        Please enter a valid email.
                      </div>
                    </div>
                    <div class="forms-inputs pb-3">
                      <label for="password" class="form-label text-14">
                        <strong>Password</strong>
                      </label>
                      <div class="pass-container">
                        <input
                          id="password"
                          autocomplete="off"
                          class="form-control text-14"
                          :type="showPass ? 'input' : 'password'"
                          placeholder="Enter password"
                          v-model="formState.password"
                          required />
                        <span
                          class="view-pass text-primary accent-light text-14 hand-cursor bg-info"
                          @click="showPass = !showPass">
                          <i v-if="showPass" class="bi bi-eye-slash me-1" id="togglePassword"></i>
                          <i v-else class="bi bi-eye me-1" id="togglePassword"></i>
                          <med>&nbsp;{{ showPass ? 'Hide' : 'Show' }}</med>
                        </span>
                      </div>
                      <div id="emailHelp" class="form-text mt-1 text-14">
                        <a href="" @click.prevent="toggleResetForm"><med>Forgot password</med></a>
                      </div>
                    </div>
                    <div class="my-4 text-center">
                      <div
                        id="invalidCredentialsMessage"
                        v-if="showError"
                        class="alert alert-danger text-center"
                        role="alert">
                        Please check your credentials!
                      </div>
                      <button
                        id="submitBUtton"
                        class="btn btn-primary w-100 header-20 mb-5"
                        :disabled="
                          usernameError ||
                          formState.username.length === 0 ||
                          formState.password.length === 0
                        ">
                        Sign In
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="row pt-5 mt-5 text-white footer-container">
      <div class="col-12 text-center">
        <p class="text-12">
          {{ new Date().getFullYear() }} All Rights Reserved. Benefits Compliance Solutions is a registered trademark. Patent Pending.
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  /* This file styles EVERY bare `span` as an absolutely positioned floating label for the
     credentials form (position, offsets, padding and font-size). Inside the two-factor step a
     span is just a span, so the whole set is reset here rather than property by property —
     the stray `padding: 5px 10px` was showing up as a double space before the email address. */
  #mfaStep span {
    position: static;
    top: auto;
    left: auto;
    padding: 0;
    font-size: inherit;
    z-index: auto;
  }

  .mfa-link-button {
    height: auto;
  }

  /* ── Spacing of the two-factor step ──────────────────────────────────────────
     Scoped to this step: the credentials and reset screens keep the layout they have today.
     Rhythm: 24px between sections, 10–14px inside them, so the hierarchy reads without
     relying on dividers. To loosen it further, raise --mfa-gap to 32px and the card's top
     padding to 48px; the inner values should stay where they are. */
  .login-shell--mfa {
    padding: 56px 24px;
  }

  .login-card-container--mfa {
    /* The card sits centred in whatever height is left over. */
    align-items: center;
  }

  .login-card--mfa {
    --mfa-gap: 24px;
    padding: 40px 48px 32px;
  }

  .login-card--mfa #mfaStep {
    display: flex;
    flex-direction: column;
    gap: var(--mfa-gap);
  }

  .mfa-logo-row {
    /* 12px of the shared rhythm plus 8px so the logo breathes before the title. */
    margin-bottom: 20px;
  }

  .mfa-form {
    display: flex;
    flex-direction: column;
    gap: var(--mfa-gap);
  }

  /* ── Header block — logo, title, destination and the escape hatch, centred and
     stacked with an even 12px rhythm. ── */
  .mfa-header {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    /* No margin here: the 20px under the logo already carries the rhythm, and adding 12 on
       top of it was pushing the title 32px down. */
    text-align: center;
  }

  .mfa-header-text {
    /* 520px keeps the sentence off the card edges (572px of usable width) while still fitting
       a real address on one line: "We sent a 6-digit code to award@mcgohanbrabender.com"
       measures 449px, and cdickerson@mcgohanbrabender.com 483px. Anything longer wraps to a
       second line rather than overflowing. */
    max-width: 520px;
    font-size: 15px;
    color: var(--bs-gray-600);
    position: static;
  }

  .mfa-account-email {
    color: var(--bs-body-color);
    font-weight: 600;
    /* Character-level break as a fallback, so a long address cannot overflow the card. */
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  .mfa-signout-link {
    color: var(--bs-primary);
    text-decoration: underline;
  }

  /* ── Verification code field ── */
  .mfa-digits {
    display: flex;
    gap: 10px;
    margin: 10px 0;
  }

  .mfa-digit {
    flex: 1 1 0;
    min-width: 0;
    height: 52px;
    border: 2px solid var(--bs-gray-300);
    border-radius: 6px;
    background-color: #fff;
    text-align: center;
    font-size: 22px;
    font-weight: 600;
    color: var(--bs-body-color);
    transition: border-color 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease;
  }

  .mfa-digit:focus {
    outline: none;
    border-color: var(--bs-primary);
    /* Soft halo on the active box. */
    box-shadow: 0 0 0 4px rgba(92, 12, 184, 0.15);
  }

  .mfa-digit--error,
  .mfa-digit--error:focus {
    border-color: var(--bs-danger);
    background-color: #fdeff1;
  }

  .mfa-digit--error:focus {
    box-shadow: 0 0 0 4px rgba(220, 53, 69, 0.15);
  }

  .mfa-help {
    position: static;
    font-size: 12px;
    color: var(--bs-gray-600);
  }

  .mfa-help--error {
    color: var(--bs-danger);
  }

  /* ── Secondary help, separated from the primary action ── */
  .mfa-footer {
    /* On top of the section gap, so there is a clear 44px between the button and the first
       line of help — that is the jump from primary action to secondary. */
    margin-top: 20px;
    border-top: 1px solid #ece8f3;
    padding-top: 14px;
    display: flex;
    flex-direction: column;
    gap: 14px;
    text-align: left;
  }

  .mfa-footer-line {
    position: static;
    font-size: 14px;
    color: var(--bs-gray-600);
  }

  /* Waiting variant: same slot, not a target. Tabular figures keep the countdown from
     shifting width as the digits change. */
  .mfa-resend-waiting {
    color: var(--bs-gray-500);
    font-variant-numeric: tabular-nums;
    font-feature-settings: 'tnum';
  }

  .mfa-resend-link {
    color: var(--bs-primary);
    text-decoration: underline;
  }

  .mfa-nowrap {
    white-space: nowrap;
  }

  body {
    background-color: #2e0c59 !important;
    height: 100vh;
  }

  body.body--light {
    color: black;
  }

  .q-parallax__media > img {
    min-width: 50% !important;
  }

  .card {
    height: 100%;
  }

  .hand-cursor {
    cursor: pointer;
  }

  .container-fluid {
    height: 100vh;
  }

  body {
    background: #000;
  }
  .card {
    border: none;
  }
  .forms-inputs {
    position: relative;
  }
  /* removed empty ruleset */

  .pass-container {
    position: relative;
  }

  .view-pass {
    position: relative;
    float: right;
    margin-right: 1rem;
    top: -2.2rem;
    border-radius: 3.2px;
    min-width: 76px;
  }

  span {
    position: absolute;
    top: -18px;
    left: 10px;
    padding: 5px 10px;
    font-size: 15px;
    z-index: 999;
  }
  .forms-inputs input {
    height: 40px;
    border: 2px solid #eee;
  }
  .forms-inputs input:focus {
    box-shadow: none;
    outline: none;
    border: 2px solid #000;
  }
  .btn {
    height: 50px;
  }
  .success-data {
    display: flex;
    flex-direction: column;
  }
  .bxs-badge-check {
    font-size: 90px;
  }
  .loading-overlay {
    position: fixed;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 999999;
    height: 100vh;
    width: 100vw;
    --bs-bg-opacity: 0.5;
  }
  .img-fluid {
    height: 57px;
  }
  .login-card {
    width: 596px;
    height: fit-content;
  }
  .login-card-container {
    height: 82%;
  }
  .footer-container {
    height: 10%;
  }
</style>